import { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp, ROLES, ROLE_LABELS } from '../../context/AppContext';
import { fetchCases, fetchCaseHistory } from '../../api';
import { STATUS_CONFIG, CASE_STATUSES } from '../../data/mockData';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Avatar, AvatarFallback } from '../../components/ui/avatar';
import { StatusBadge } from '../../components/shared/StatusBadge';
import {
  ArrowLeft,
  Calendar,
  Filter,
  User,
  Activity,
  UserPlus,
  ClipboardCheck,
  ShieldAlert,
  Archive,
  RefreshCw,
  Clock,
  Compass,
  FileText,
  Trash2,
  CheckCircle,
  XCircle,
  AlertTriangle,
  History,
} from 'lucide-react';
import { format, differenceInDays } from 'date-fns';

export default function CaseHistory() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentRole, currentUserId, caseStatusOverrides } = useApp();

  const [loading, setLoading] = useState(true);
  const [caseItem, setCaseItem] = useState(null);
  const [logs, setLogs] = useState([]);

  // Filters State
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [actionTypeFilter, setActionTypeFilter] = useState('all');
  const [userNameFilter, setUserNameFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Load Data
  const loadHistoryData = useCallback(async () => {
    setLoading(true);
    try {
      // Load case details to match ID
      const casesList = await fetchCases().catch(() => []);
      const matchedCase = casesList.find(c => c.id === id || c.refNumber === id) || null;
      setCaseItem(matchedCase);

      // Load history logs from API
      const historyRes = await fetchCaseHistory(id, currentUserId, currentRole).catch(() => null);
      if (historyRes && Array.isArray(historyRes.logs)) {
        setLogs(historyRes.logs);
      } else {
        setLogs([]);
      }
    } catch (error) {
      console.error("Failed to load case history logs", error);
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [id, currentUserId, currentRole]);

  useEffect(() => {
    loadHistoryData();
  }, [loadHistoryData]);

  // Derived Case Status
  const currentCaseStatus = caseItem ? (caseStatusOverrides[caseItem.id] || caseItem.status) : '';

  // Filtered Logs list
  const filteredLogs = useMemo(() => {
    return logs.filter(log => {
      // 1. Date Range Filter
      if (startDate) {
        const logDate = new Date(log.date_time);
        const filterStart = new Date(startDate);
        if (logDate < filterStart) return false;
      }
      if (endDate) {
        const logDate = new Date(log.date_time);
        const filterEnd = new Date(endDate);
        // set to end of day
        filterEnd.setHours(23, 59, 59, 999);
        if (logDate > filterEnd) return false;
      }

      // 2. Action Type Filter
      if (actionTypeFilter !== 'all') {
        const type = log.action_type.toLowerCase();
        if (actionTypeFilter === 'assignment' && !type.includes('assign')) return false;
        if (actionTypeFilter === 'verification' && !type.includes('verification') && !type.includes('draft')) return false;
        if (actionTypeFilter === 'qc' && !type.includes('qc')) return false;
        if (actionTypeFilter === 'penalty' && !type.includes('penalty')) return false;
        if (actionTypeFilter === 'case' && type.includes('assign') && type.includes('verification') && type.includes('qc') && type.includes('penalty')) return false;
      }

      // 3. User Name Filter
      if (userNameFilter && log.new_user) {
        if (!log.new_user.toLowerCase().includes(userNameFilter.toLowerCase())) return false;
      }

      // 4. Role Filter
      if (roleFilter !== 'all') {
        if (log.role !== roleFilter) return false;
      }

      // 5. Status Filter
      if (statusFilter !== 'all') {
        // Since logs record status transitions in remarks or action types, we map statuses
        const remarks = (log.remarks || '').toLowerCase();
        const actionType = log.action_type.toLowerCase();
        if (!remarks.includes(statusFilter) && !actionType.includes(statusFilter)) return false;
      }

      return true;
    });
  }, [logs, startDate, endDate, actionTypeFilter, userNameFilter, roleFilter, statusFilter]);

  // KPI calculations
  const kpis = useMemo(() => {
    if (!logs || logs.length === 0) {
      return { assignments: 0, reassignments: 0, statusChanges: 0, qcActions: 0, daysOpen: 0 };
    }

    const assignments = logs.filter(l => l.action_type === 'Assigned' || l.action_type === 'Reassigned' || l.action_type === 'Auto Assigned').length;
    const reassignments = logs.filter(l => (l.action_type === 'Assigned' || l.action_type === 'Reassigned') && l.prev_user !== null).length;
    
    // Status changes are recorded under QC Approved, QC Rejected, Verification Submitted, Case Closed, etc.
    const statusChanges = logs.filter(l => 
      ['Case Created', 'Verification Submitted', 'QC Approved', 'QC Rejected', 'Case Closed', 'Revisit'].some(act => l.action_type.includes(act))
    ).length;

    const qcActions = logs.filter(l => l.action_type.startsWith('QC ') || l.action_type.includes('QC')).length;

    // Days open: difference between created log and closed log (or today)
    const createdLog = logs.find(l => l.action_type === 'Case Created') || logs[logs.length - 1];
    const closedLog = logs.find(l => l.action_type === 'Case Closed') || { date_time: new Date().toISOString() };
    
    let days = 0;
    if (createdLog) {
      days = differenceInDays(new Date(closedLog.date_time), new Date(createdLog.date_time));
      if (days < 0) days = 0;
    }

    return {
      assignments,
      reassignments,
      statusChanges,
      qcActions,
      daysOpen: days || 1
    };
  }, [logs]);

  // Render Timeline Icon & Colors based on action type
  const getTimelineEventStyle = (actionType) => {
    const type = actionType.toLowerCase();
    if (type.includes('create') || type.includes('import')) {
      return { icon: FileText, color: 'text-blue-600 bg-blue-50 border-blue-200' };
    }
    if (type.includes('assign')) {
      return { icon: UserPlus, color: 'text-indigo-600 bg-indigo-50 border-indigo-200' };
    }
    if (type.includes('start') || type.includes('draft')) {
      return { icon: Compass, color: 'text-amber-600 bg-amber-50 border-amber-200' };
    }
    if (type.includes('submit')) {
      return { icon: ClipboardCheck, color: 'text-teal-600 bg-teal-50 border-teal-200' };
    }
    if (type.includes('approve') || type.includes('closed')) {
      return { icon: CheckCircle, color: 'text-emerald-600 bg-emerald-50 border-emerald-200' };
    }
    if (type.includes('reject') || type.includes('correction')) {
      return { icon: XCircle, color: 'text-rose-600 bg-rose-50 border-rose-200' };
    }
    if (type.includes('penalty')) {
      return { icon: ShieldAlert, color: 'text-rose-600 bg-red-50 border-red-200' };
    }
    return { icon: Activity, color: 'text-slate-600 bg-slate-50 border-slate-200' };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="flex flex-col items-center gap-3">
          <RefreshCw className="w-8 h-8 text-blue-600 animate-spin" />
          <p className="text-sm font-medium text-slate-500">Loading case timeline history...</p>
        </div>
      </div>
    );
  }

  if (!caseItem) {
    return (
      <div className="crm-card p-8 text-center max-w-md mx-auto mt-20 border border-slate-200 text-slate-800">
        <AlertTriangle className="w-12 h-12 text-rose-500 mx-auto mb-3" />
        <h2 className="text-lg font-bold text-slate-900">Case Not Found</h2>
        <p className="text-sm text-slate-500 mt-2">The case ID you requested does not exist or has been deleted.</p>
        <Button onClick={() => navigate('/cases')} className="mt-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold">
          Back to Case Management
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in text-slate-800" data-testid="case-history-page">
      {/* Page Header */}
      <div className="flex items-center justify-between border-b border-slate-200 pb-4">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/cases')}
            className="text-slate-500 hover:text-slate-900 bg-white border border-slate-200 rounded-lg shadow-2xs hover:bg-slate-50"
            data-testid="history-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Case Timeline & Assignment Logs</h1>
              <Badge className="bg-slate-100 text-slate-800 border-none font-bold uppercase text-[10px]">
                Audit Trail
              </Badge>
            </div>
            <p className="text-xs text-slate-500 mt-1">
              View complete status movement, reallocation logs, and event history for Case ID: <span className="font-semibold text-slate-700">{caseItem.refNumber}</span>
            </p>
          </div>
        </div>
        <Button onClick={loadHistoryData} variant="outline" className="bg-white border-slate-200 text-xs gap-1.5 font-semibold py-1.5 h-auto">
          <RefreshCw className="w-3.5 h-3.5" />
          Refresh
        </Button>
      </div>

      {/* Quick Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="crm-card p-4 bg-white border border-slate-200 shadow-2xs">
          <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Total Assignments</p>
          <p className="text-xl font-bold text-slate-900 mt-1.5">{kpis.assignments}</p>
        </div>
        <div className="crm-card p-4 bg-white border border-slate-200 shadow-2xs">
          <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Reassignments</p>
          <p className="text-xl font-bold text-indigo-600 mt-1.5">{kpis.reassignments}</p>
        </div>
        <div className="crm-card p-4 bg-white border border-slate-200 shadow-2xs">
          <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Status Transitions</p>
          <p className="text-xl font-bold text-teal-600 mt-1.5">{kpis.statusChanges}</p>
        </div>
        <div className="crm-card p-4 bg-white border border-slate-200 shadow-2xs">
          <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">QC Actions</p>
          <p className="text-xl font-bold text-amber-600 mt-1.5">{kpis.qcActions}</p>
        </div>
        <div className="crm-card p-4 bg-white border border-slate-200 shadow-2xs">
          <p className="text-[10px] text-slate-500 font-semibold uppercase tracking-wider">Days Open / TAT</p>
          <p className="text-xl font-bold text-slate-900 mt-1.5">{kpis.daysOpen} {kpis.daysOpen === 1 ? 'Day' : 'Days'}</p>
        </div>
      </div>

      {/* Case Overview Header Panel */}
      <div className="crm-card p-5 bg-white border border-slate-200 shadow-2xs space-y-4">
        <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2">Case Overview Details</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 text-xs">
          <div>
            <span className="text-slate-400 font-medium block">Case ID</span>
            <span className="font-bold text-slate-800 block mt-0.5">{caseItem.refNumber}</span>
          </div>
          <div>
            <span className="text-slate-400 font-medium block">Applicant Name</span>
            <span className="font-bold text-slate-800 block mt-0.5">{caseItem.applicantName}</span>
          </div>
          <div>
            <span className="text-slate-400 font-medium block">Product</span>
            <span className="font-semibold text-slate-800 block mt-0.5">{caseItem.product?.name || 'Address Verification'}</span>
          </div>
          <div>
            <span className="text-slate-400 font-medium block">Verification Type</span>
            <span className="text-slate-600 block mt-0.5">{caseItem.verificationType || 'Residence Verification'}</span>
          </div>
          <div>
            <span className="text-slate-400 font-medium block">Current Status</span>
            <span className="block mt-0.5">
              <StatusBadge
                status={STATUS_CONFIG[currentCaseStatus]?.color || 'neutral'}
                label={STATUS_CONFIG[currentCaseStatus]?.label || currentCaseStatus}
                size="sm"
              />
            </span>
          </div>
          <div>
            <span className="text-slate-400 font-medium block">Assignee</span>
            <span className="font-medium text-slate-800 block mt-0.5">
              {caseItem.assignedTo ? caseItem.assignedTo.name : 'Unassigned'}
            </span>
          </div>
          <div>
            <span className="text-slate-400 font-medium block">Created Date</span>
            <span className="text-slate-600 block mt-0.5">
              {caseItem.createdAt ? format(new Date(caseItem.createdAt), 'dd-MMM-yyyy hh:mm a') : 'N/A'}
            </span>
          </div>
        </div>
      </div>

      {/* Main Grid: Filters on Left/Top, Timeline in Center/Right */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        
        {/* Filters Panel */}
        <div className="space-y-4">
          <div className="crm-card p-5 bg-white border border-slate-200 shadow-2xs space-y-4">
            <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2 flex items-center gap-2">
              <Filter className="w-4 h-4 text-blue-500" />
              History Filters
            </h3>

            {/* Date Range */}
            <div className="space-y-3">
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-slate-600">Start Date</Label>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="bg-slate-50 border-slate-200 text-xs text-slate-700 h-9"
                />
              </div>
              <div className="space-y-1">
                <Label className="text-xs font-semibold text-slate-600">End Date</Label>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="bg-slate-50 border-slate-200 text-xs text-slate-700 h-9"
                />
              </div>
            </div>

            {/* Event Category */}
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Event Action Category</Label>
              <select
                value={actionTypeFilter}
                onChange={(e) => setActionTypeFilter(e.target.value)}
                className="w-full rounded-md border border-slate-200 bg-slate-50 p-2 text-xs font-medium text-slate-700 outline-none h-9"
              >
                <option value="all">All Events</option>
                <option value="assignment">Assignments Only</option>
                <option value="verification">Verification Progress</option>
                <option value="qc">QC Audit Triggers</option>
                <option value="penalty">Penalties</option>
              </select>
            </div>

            {/* User Name Filter */}
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Actor Name</Label>
              <Input
                placeholder="Search user name..."
                value={userNameFilter}
                onChange={(e) => setUserNameFilter(e.target.value)}
                className="bg-slate-50 border-slate-200 text-xs text-slate-700 h-9"
              />
            </div>

            {/* Role Filter */}
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Actor Role</Label>
              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="w-full rounded-md border border-slate-200 bg-slate-50 p-2 text-xs font-medium text-slate-700 outline-none h-9"
              >
                <option value="all">All Roles</option>
                <option value="admin">Admin</option>
                <option value="supreme_manager">Supreme Manager</option>
                <option value="manager">Manager</option>
                <option value="vendor">Vendor</option>
                <option value="field_executive">Field Executive</option>
                <option value="quality_checker">Quality Checker</option>
                <option value="client">Client</option>
              </select>
            </div>

            {/* Status Transition Filter */}
            <div className="space-y-1">
              <Label className="text-xs font-semibold text-slate-600">Containing Status</Label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-md border border-slate-200 bg-slate-50 p-2 text-xs font-medium text-slate-700 outline-none h-9"
              >
                <option value="all">All Statuses</option>
                {Object.entries(CASE_STATUSES).map(([key, val]) => (
                  <option key={key} value={val}>{STATUS_CONFIG[val]?.label || val}</option>
                ))}
              </select>
            </div>

            {/* Clear Button */}
            <Button
              variant="outline"
              className="w-full text-xs border-slate-200 h-8 font-semibold bg-transparent hover:bg-slate-50 mt-2"
              onClick={() => {
                setStartDate('');
                setEndDate('');
                setActionTypeFilter('all');
                setUserNameFilter('');
                setRoleFilter('all');
                setStatusFilter('all');
              }}
            >
              Reset Filters
            </Button>
          </div>
        </div>

        {/* Timeline Panel */}
        <div className="lg:col-span-3 space-y-6">
          <div className="crm-card p-6 bg-white border border-slate-200 shadow-2xs">
            <h3 className="text-base font-bold text-slate-900 border-b border-slate-100 pb-3 mb-6 flex items-center gap-2">
              <History className="w-5 h-5 text-blue-600" />
              Activity & Assignment Movement History ({filteredLogs.length})
            </h3>

            {filteredLogs.length === 0 ? (
              <div className="text-center py-20 text-slate-400 text-sm border border-dashed border-slate-200 rounded-lg bg-slate-50">
                <Clock className="w-10 h-10 text-slate-300 mx-auto mb-2" />
                No history logs match the selected filter criteria.
              </div>
            ) : (
              <div className="relative border-l-2 border-slate-200 ml-6 pl-8 space-y-8 py-2">
                {filteredLogs.map((log) => {
                  const style = getTimelineEventStyle(log.action_type);
                  const Icon = style.icon;
                  
                  return (
                    <div key={log.id} className="relative group text-sm">
                      {/* Timeline Dot */}
                      <span className={`absolute -left-[49px] top-0.5 w-10 h-10 rounded-full border-2 flex items-center justify-center shadow-xs transition-transform group-hover:scale-105 z-10 ${style.color}`}>
                        <Icon className="w-4 h-4" />
                      </span>

                      {/* Timeline Card */}
                      <div className="p-4 rounded-xl border border-slate-100 bg-slate-50/50 hover:bg-slate-50 shadow-sm transition-all hover:border-slate-200 space-y-2">
                        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-1.5">
                          <div>
                            <span className="text-xs font-bold uppercase tracking-wider text-slate-500 block">
                              {log.action_type}
                            </span>
                            <span className="text-xs font-medium text-slate-400">
                              IP: {log.ip_address || '127.0.0.1'}
                            </span>
                          </div>
                          <div className="text-left sm:text-right">
                            <span className="text-xs font-bold text-slate-700 bg-white border border-slate-200/80 px-2.5 py-1 rounded-lg inline-flex items-center gap-1.5">
                              <User className="w-3.5 h-3.5 text-slate-500" />
                              {log.new_user} 
                              <Badge className="bg-slate-100 text-slate-500 text-[9px] uppercase border-none hover:bg-slate-100 px-1 font-bold">
                                {ROLE_LABELS[log.role] || log.role}
                              </Badge>
                            </span>
                            <p className="text-[10px] text-slate-400 font-semibold mt-1">
                              {log.date_time ? format(new Date(log.date_time.replace(' ', 'T')), 'dd-MMM-yyyy hh:mm a') : 'N/A'}
                            </p>
                          </div>
                        </div>

                        {/* Assignment detail trail */}
                        {log.prev_user && (
                          <p className="text-xs text-slate-500 font-medium">
                            <strong className="font-semibold text-slate-600">Reallocated From:</strong> {log.prev_user}
                          </p>
                        )}

                        <div className="bg-white border border-slate-100/60 rounded-lg p-2.5 text-xs text-slate-600 leading-relaxed shadow-3xs">
                          {log.remarks}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          
          {/* Detailed Tabular View Tab */}
          <div className="crm-card p-6 bg-white border border-slate-200 shadow-2xs space-y-4">
            <h3 className="text-sm font-bold text-slate-900 border-b border-slate-100 pb-2">Tabular Audit Log (Read-Only)</h3>
            <div className="overflow-x-auto rounded-lg border border-slate-100">
              <table className="w-full text-xs text-left border-collapse">
                <thead className="bg-slate-50 border-b border-slate-200 text-slate-500">
                  <tr>
                    <th className="px-4 py-2.5 font-bold uppercase tracking-wider">Date & Time</th>
                    <th className="px-4 py-2.5 font-bold uppercase tracking-wider">Action Type</th>
                    <th className="px-4 py-2.5 font-bold uppercase tracking-wider">Previous User</th>
                    <th className="px-4 py-2.5 font-bold uppercase tracking-wider">New User</th>
                    <th className="px-4 py-2.5 font-bold uppercase tracking-wider">Role</th>
                    <th className="px-4 py-2.5 font-bold uppercase tracking-wider">IP Address</th>
                    <th className="px-4 py-2.5 font-bold uppercase tracking-wider">Remarks</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 bg-white">
                  {filteredLogs.map((log) => (
                    <tr key={log.id} className="hover:bg-slate-50/20 font-medium">
                      <td className="px-4 py-3 text-slate-600 whitespace-nowrap">
                        {log.date_time ? format(new Date(log.date_time.replace(' ', 'T')), 'dd-MMM-yyyy hh:mm a') : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <span className="font-bold text-slate-800">{log.action_type}</span>
                      </td>
                      <td className="px-4 py-3 text-slate-500">{log.prev_user || '-'}</td>
                      <td className="px-4 py-3 text-slate-800 font-bold">{log.new_user}</td>
                      <td className="px-4 py-3 text-slate-500 capitalize">{ROLE_LABELS[log.role] || log.role}</td>
                      <td className="px-4 py-3 text-slate-400">{log.ip_address}</td>
                      <td className="px-4 py-3 text-slate-600 leading-relaxed min-w-[200px] whitespace-normal break-words">{log.remarks}</td>
                    </tr>
                  ))}
                  {filteredLogs.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-6 text-slate-400">No logs found.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
